import { Fragment, useEffect, useState, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Descriptions, Popover, Space, Typography } from 'antd';
import { useIsMobile } from '../hooks/useMobile';

export type EvalStickyRule = { title: string; detail: string };

const ORDER_CN = ['一', '二', '三', '四', '五', '六', '七', '八'] as const;

function orderLabel(i: number) {
  return (ORDER_CN[i] ?? `${i + 1}`) as string;
}

export type EvalTaskStickyHeaderProps = {
  /** 左上场景名，Tooltip 灰字里也用它 */
  pageTitle: string;
  evaluatee: { name: string; deptPath?: string | null; position?: string | null };
  roleTag: ReactNode;
  /** Descriptions 「批次截止」展示串，已由调用方格式化 */
  batchDeadlineLabel?: string | null;
  /** 可选：批次展示名（如本轮盘点标题） */
  batchName?: string | null;
  /** Descriptions 「状态」格整格自定义 */
  statusArea: ReactNode;
  /** 浅色规则条左上角粗体文案 */
  rulesSectionHeading: string;
  /** Popover 内大字标题 */
  rulesPopoverHeading: string;
  rules: EvalStickyRule[];
  /** Card / Modal body 的水平内边距，用于负边距铺满 */
  bodyPaddingPx?: number;
};

function readMobileHeaderBottom(): number {
  const header = document.querySelector('.tg-mobile-header') as HTMLElement | null;
  if (header) {
    return Math.round(header.getBoundingClientRect().bottom);
  }
  // 兜底：与 Layout 里 Header minHeight 对齐
  return 64;
}

function NameRowLabel({ name }: { name: string }) {
  return (
    <>
      <Typography.Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
        正在评价：
      </Typography.Text>
      <div style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35, color: '#262626' }}>
        {name}
      </div>
    </>
  );
}

const nameRowFlexStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  padding: '10px 14px 9px',
  background: '#fff',
  borderBottom: '1px solid rgba(124, 92, 191, 0.12)',
  boxSizing: 'border-box',
};

/**
 * 手机端：原位姓名行 + IntersectionObserver 触发后 Portal 浮条。
 * 不依赖 CSS sticky（祖先 overflow 在钉钉里会让 sticky 失效）。
 */
function MobileEvalNameHeader({
  evaluateeName,
  deptPositionLabel,
  roleTag,
  statusArea,
  rulesSectionHeading,
  rulesPopoverHeading,
  rulesPopoverBody,
  rulesCount,
  batchDeadlineLabel,
  deadline,
}: {
  evaluateeName: string;
  deptPositionLabel: string;
  roleTag: ReactNode;
  statusArea: ReactNode;
  rulesSectionHeading: string;
  rulesPopoverHeading: string;
  rulesPopoverBody: ReactNode;
  rulesCount: number;
  batchDeadlineLabel?: string | null;
  deadline: string;
}) {
  const mobilePad = 14;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const [floatTop, setFloatTop] = useState(64);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    let observer: IntersectionObserver | null = null;

    const attach = () => {
      observer?.disconnect();
      const headerBottom = readMobileHeaderBottom();
      setFloatTop(headerBottom);
      observer = new IntersectionObserver(
        ([entry]) => {
          setStuck(!entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: `-${headerBottom}px 0px 0px 0px`,
          threshold: 0,
        },
      );
      observer.observe(sentinel);
    };

    attach();
    window.addEventListener('resize', attach);
    window.addEventListener('orientationchange', attach);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', attach);
      window.removeEventListener('orientationchange', attach);
    };
  }, []);

  const floatBar =
    stuck && typeof document !== 'undefined'
      ? createPortal(
          <div
            style={{
              ...nameRowFlexStyle,
              position: 'fixed',
              top: floatTop,
              left: 'calc(12px + env(safe-area-inset-left, 0px))',
              right: 'calc(12px + env(safe-area-inset-right, 0px))',
              zIndex: 90,
              boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
              borderRadius: '0 0 10px 10px',
            }}
          >
            <NameRowLabel name={evaluateeName} />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {/* 1px 哨兵：滚出导航下方后触发浮条 */}
      <div
        ref={sentinelRef}
        aria-hidden="true"
        style={{ height: 1, marginTop: -mobilePad, marginBottom: -1, pointerEvents: 'none' }}
      />
      <div
        style={{
          ...nameRowFlexStyle,
          marginLeft: -mobilePad,
          marginRight: -mobilePad,
          // stuck 时隐藏但保高度，避免读屏双读 / 布局跳变
          visibility: stuck ? 'hidden' : 'visible',
        }}
        aria-hidden={stuck}
      >
        <NameRowLabel name={evaluateeName} />
      </div>
      {floatBar}

      <div
        style={{
          padding: '12px 0',
          marginBottom: 12,
          borderBottom: '1px solid rgba(124, 92, 191, 0.12)',
        }}
      >
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, display: 'block', marginBottom: 8, lineHeight: 1.45 }}
        >
          {deptPositionLabel}
        </Typography.Text>
        <Space size={8} wrap style={{ width: '100%', marginBottom: 10 }}>
          {roleTag}
          {statusArea}
        </Space>
        <Popover
          zIndex={4050}
          title={null}
          trigger={['click']}
          placement="bottom"
          getPopupContainer={() => document.body}
          styles={{ root: { maxWidth: 320 } }}
          content={
            <div>
              <Typography.Text
                strong
                style={{ fontSize: 14, display: 'block', marginBottom: 10 }}
              >
                {rulesPopoverHeading}
              </Typography.Text>
              {rulesPopoverBody}
            </div>
          }
        >
          <div
            role="button"
            tabIndex={0}
            style={{
              padding: '8px 10px',
              background: 'linear-gradient(180deg, #f3eeff 0%, #faf8ff 100%)',
              borderRadius: 8,
              border: '1px solid rgba(124, 92, 191, 0.18)',
              cursor: 'pointer',
              fontSize: 12,
              color: '#5b3fa8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>
              <Typography.Text strong style={{ fontSize: 12 }}>
                {rulesSectionHeading}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                · 共 {rulesCount} 条
              </Typography.Text>
            </span>
            <span style={{ fontSize: 11 }}>点击展开 ▾</span>
          </div>
        </Popover>
        {batchDeadlineLabel ? (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, display: 'block', marginTop: 8 }}
          >
            批次截止：{deadline}
          </Typography.Text>
        ) : null}
      </div>
    </>
  );
}

/**
 * 素质/胜任力等评价页共用：大字标题 · 基本信息 · 一至多条规则提要 + Popover 全文，整体 sticky 吸顶。
 */
export function EvalTaskStickyHeader({
  pageTitle,
  evaluatee,
  roleTag,
  batchDeadlineLabel,
  batchName,
  statusArea,
  rulesSectionHeading,
  rulesPopoverHeading,
  rules,
  bodyPaddingPx = 24,
}: EvalTaskStickyHeaderProps) {
  const b = bodyPaddingPx;
  const isMobile = useIsMobile();

  const evaluateeTip = (
    <div style={{ maxWidth: 300, whiteSpace: 'normal', lineHeight: 1.65, fontSize: 13 }}>
      <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>{pageTitle}</div>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>{evaluatee.name}</div>
      {batchName != null && batchName !== '' ? (
        <div style={{ marginBottom: 4 }}>批次：{batchName}</div>
      ) : null}
      <div>部门：{evaluatee.deptPath ?? '—'}</div>
      <div>职位：{evaluatee.position ?? '—'}</div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#595959' }}>
        当前角色：{roleTag}
      </div>
    </div>
  );

  const rulesPopoverBody = (
    <div style={{ maxWidth: 400, maxHeight: 380, overflowY: 'auto' }}>
      {rules.map((r, idx) => (
        <div key={`${idx}-${r.title}`} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {orderLabel(idx)}、{r.title}
          </div>
          <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.65 }}>{r.detail}</div>
        </div>
      ))}
    </div>
  );

  const deadline =
    batchDeadlineLabel != null && batchDeadlineLabel !== '' ? batchDeadlineLabel : '—';

  /**
   * 手机端只冻结「正在评价：姓名」这一行。
   * 部门、职位、角色、状态、规则与截止日期均留在普通流，滚动后释放屏幕空间。
   */
  if (isMobile) {
    const deptPositionLabel =
      [evaluatee.deptPath, evaluatee.position].filter(Boolean).join(' · ') || '—';

    return (
      <MobileEvalNameHeader
        evaluateeName={evaluatee.name}
        deptPositionLabel={deptPositionLabel}
        roleTag={roleTag}
        statusArea={statusArea}
        rulesSectionHeading={rulesSectionHeading}
        rulesPopoverHeading={rulesPopoverHeading}
        rulesPopoverBody={rulesPopoverBody}
        rulesCount={rules.length}
        batchDeadlineLabel={batchDeadlineLabel}
        deadline={deadline}
      />
    );
  }

  return (
    <div
      style={{
        position: 'sticky',
        // PC 真正的滚动容器是 .tg-app-content，导航在容器外，所以 top:0 即贴在导航下方。
        top: 0,
        zIndex: 48,
        marginLeft: -b,
        marginRight: -b,
        marginTop: -b,
        padding: `${Math.max(16, b - 4)}px ${b}px 14px`,
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
      }}
    >
      <div className="tg-page-title" style={{ marginTop: 0, marginBottom: 14 }}>
        <Popover
          zIndex={4200}
          trigger={['hover', 'click']}
          placement="bottomLeft"
          mouseEnterDelay={0.12}
          getPopupContainer={() => document.body}
          content={evaluateeTip}
        >
          <span
            style={{
              cursor: 'pointer',
              borderBottom: '1px dashed rgba(0,0,0,0.35)',
            }}
          >
            {pageTitle} · {evaluatee.name}
          </span>
        </Popover>
        {roleTag}
      </div>

      <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
        <Descriptions.Item label="被评价人">
          <Popover
            zIndex={4200}
            trigger={['hover', 'click']}
            placement="bottomLeft"
            mouseEnterDelay={0.12}
            getPopupContainer={() => document.body}
            content={evaluateeTip}
          >
            <span
              style={{
                cursor: 'pointer',
                borderBottom: '1px dashed rgba(22,119,255,0.55)',
                color: '#1677ff',
              }}
            >
              {evaluatee.name}
            </span>
          </Popover>
        </Descriptions.Item>
        <Descriptions.Item label="部门">{evaluatee.deptPath ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="职位">{evaluatee.position ?? '—'}</Descriptions.Item>
        {batchName != null && batchName !== '' ? (
          <Descriptions.Item label="批次" span={3}>
            {batchName}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="批次截止">
          {deadline}
        </Descriptions.Item>
        <Descriptions.Item label="状态" span={2}>
          {statusArea}
        </Descriptions.Item>
      </Descriptions>

      <div
        style={{
          marginTop: 16,
          padding: '12px 14px',
          background: 'linear-gradient(180deg, #f0f5ff 0%, #fafbff 100%)',
          borderRadius: 8,
          border: '1px solid #d6e4ff',
        }}
      >
        <Popover
          zIndex={4050}
          title={null}
          trigger={['hover', 'click']}
          placement="bottomLeft"
          mouseEnterDelay={0.12}
          getPopupContainer={() => document.body}
          styles={{ root: { maxWidth: 428 } }}
          content={
            <div>
              <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                {rulesPopoverHeading}
              </Typography.Text>
              {rulesPopoverBody}
            </div>
          }
        >
          <div role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
            <Typography.Text strong style={{ fontSize: 13, display: 'inline' }}>
              {rulesSectionHeading}
            </Typography.Text>
            <Typography.Text style={{ marginLeft: 10, fontSize: 11, color: '#1677ff' }}>
              悬停或点击展开浮窗
            </Typography.Text>
            <div style={{ fontSize: 12, lineHeight: 1.85, color: '#262626', marginTop: 8 }}>
              {rules.map((r, idx) => (
                <Fragment key={`${idx}-${r.title}`}>
                  {idx > 0 ? (
                    <span aria-hidden="true" style={{ color: '#8c8c8c', userSelect: 'none' }}>
                      ；
                    </span>
                  ) : null}
                  <span style={{ color: '#1677ff' }}>
                    {orderLabel(idx)}、{r.title}
                  </span>
                </Fragment>
              ))}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              细则以浮窗全文为准
            </Typography.Text>
          </div>
        </Popover>
      </div>
    </div>
  );
}
